import { useState } from "react";
import {
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";

export default function TreatmentModalComponent({
  patient,
  onClose,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [treatmentName, setTreatmentName] = useState("");
  const [amount, setAmount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [errors, setErrors] = useState({});
  const [paymentMode, setPaymentMode] = useState("");
  const [hasTodayVisit, setHasTodayVisit] = useState(false);
  const checkTodayVisit = async (date) => {
  const q = query(
    collection(db, "visits"),
    where("patient_id", "==", patient.id)
  );

  const snap = await getDocs(q);

  const selectedDate = new Date(date).toDateString();

  let exists = false;

  snap.forEach((d) => {
    const data = d.data();

    const visitDate = data.created_at?.toDate
      ? data.created_at.toDate()
      : new Date(data.created_at);

    if (
      visitDate.toDateString() === selectedDate &&
      data.token_number &&
      data.token_number !== "TRC"
    ) {
      exists = true;
    }
  });

  setHasTodayVisit(exists);
};

  // ✅ check if selected date is today
const selectedIsToday =
  startDate &&
  new Date(startDate).toDateString() ===
    new Date().toDateString();

const shouldAskAmount =
  selectedIsToday && hasTodayVisit;

  // ✅ form validation
  const validate = () => {
    const newErrors = {};

    if (!startDate) {
      newErrors.date = "Date is mandatory";
    }

   if (shouldAskAmount && !amount) {
  newErrors.amount = "Today's amount is required";
}

if (shouldAskAmount && !paymentMode) {
  newErrors.paymentMode = "Payment mode is required";
}

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ disable button logic
  const isFormValid =
  startDate &&
  (
    !shouldAskAmount ||
    (amount && paymentMode)
  );

  const handleStartTreatment = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      // 1️⃣ Create Treatment
      const treatmentRef = await addDoc(collection(db, "treatments"), {
        patient_id: patient.id,
        vpjc_id: patient.vpjc_id,
        treatment_name: treatmentName || "",
        start_date: new Date(startDate),
        status: "ACTIVE",
        created_at: serverTimestamp(),
      });
      const branch =
  typeof patient.branch_name === "object"
    ? patient.branch_name.name
    : patient.branch_name;
      // 2️⃣ Add entry ONLY if today
      if (shouldAskAmount) {
await addDoc(collection(db, "treatment_entries"), {
  treatment_id: treatmentRef.id,
  patient_id: patient.id,
  date: serverTimestamp(),
  amount: Number(amount),
  payment_mode: paymentMode,
  branch_name: branch,
  created_at: serverTimestamp(),
});
      }

      // 3️⃣ Update patient
      await updateDoc(doc(db, "patients", patient.id), {
        treatment_active: true,
        current_treatment_id: treatmentRef.id,
        current_treatment_name: treatmentName || "Treatment",
        updated_at: serverTimestamp(),
      });

      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setErrors({ general: "Something went wrong" });
    }

    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg">
        <h2 className="text-lg font-semibold text-[#204080] mb-4">
          Start Treatment
        </h2>

        {/* Date */}
        <input
          type="date"
          value={startDate}
          onChange={async (e) => {
  const value = e.target.value;

  setStartDate(value);

  setErrors((prev) => ({
    ...prev,
    date: "",
  }));

  if (value) {
    await checkTodayVisit(value);
  }
}}
          className={`w-full border rounded px-3 py-2 mb-1 text-sm ${
            errors.date ? "border-red-500" : ""
          }`}
        />
        {errors.date && (
          <p className="text-red-500 text-xs mb-2">{errors.date}</p>
        )}

        {/* Treatment Name (optional) */}
        <input
          type="text"
          placeholder="Treatment Name (optional)"
          value={treatmentName}
          onChange={(e) => setTreatmentName(e.target.value)}
          className="w-full border rounded px-3 py-2 mb-3 text-sm"
        />

        {/* Amount ONLY if today */}
        
        {shouldAskAmount && (
  <>
    <input
      type="text"
      inputMode="numeric"
      placeholder="Enter today's amount"
      value={amount}
      onChange={(e) => {
        const val = e.target.value.replace(/[^0-9]/g, "");
        setAmount(val);
        setErrors((prev) => ({ ...prev, amount: "" }));
      }}
      className="w-full border rounded px-3 py-2 mb-1 text-sm"
    />

    {errors.amount && (
      <p className="text-red-500 text-xs mb-2">
        {errors.amount}
      </p>
    )}

    <select
      value={paymentMode}
      onChange={(e) => {
        setPaymentMode(e.target.value);
        setErrors((prev) => ({
          ...prev,
          paymentMode: "",
        }));
      }}
      className={`w-full border rounded px-3 py-2 mb-1 text-sm font-semibold
        ${
          paymentMode === "ONLINE"
            ? "text-blue-700 border-blue-300"
            : paymentMode === "CASH"
              ? "text-green-700 border-green-300"
              : "text-gray-700 border-gray-300"
        }`}
    >
      <option value="">Select Payment Mode</option>
      <option value="CASH">Cash</option>
      <option value="ONLINE">Online</option>
    </select>

    {errors.paymentMode && (
      <p className="text-red-500 text-xs mb-2">
        {errors.paymentMode}
      </p>
    )}
  </>
)}
        {selectedIsToday && !hasTodayVisit && (
  <p className="text-xs text-gray-500 mb-2">
    Payment will be collected when today's token is created.
  </p>
)}
        {/* General Error */}
        {errors.general && (
          <p className="text-red-500 text-xs mb-2">
            {errors.general}
          </p>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded text-sm"
          >
            Cancel
          </button>

          <button
            onClick={handleStartTreatment}
            disabled={!isFormValid || loading}
            className={`px-4 py-2 text-sm rounded text-white ${
              !isFormValid || loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Saving..." : "Start"}
          </button>
        </div>
      </div>
    </div>
  );
}